import { useState } from 'react';
import TasksTable from './task-table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import TaskDetail from './detail';
import ServerlessTaskSearch, { FormSchemaFilter } from './task-search';
import { useListServerlessTasks } from '@/service/serverless-gpu/hook';
import { columnsServerlessTask } from './task-table/type';
import { ServerlessTask } from '@/service/serverless-gpu/type';
import dayjs from 'dayjs';
import { isDateInRange, compareDateDesc } from '@/utils/date';
import { useServerlessStore } from '@/store/serverless';
import { Button } from '@/components/ui/button';
import { RefreshCwIcon } from 'lucide-react';

const ServerlessTasks = () => {
  const [filter, setFilter] = useState<FormSchemaFilter>();
  const { token } = useServerlessStore();

  const { data, isLoading, refetch } = useListServerlessTasks(
    {},
    {
      enabled: !!token,
    }
  );

  const filterData = (
    data: ServerlessTask[],
    filter: FormSchemaFilter = {}
  ) => {
    return data.filter((item) => {
      if (filter?.taskId && !item.id.includes(filter.taskId)) return false;
      if (
        filter?.status &&
        filter?.status?.length > 0 &&
        !filter.status.includes(item.status)
      )
        return false;
      if (filter?.type && item.trigger_type !== filter.type) return false;
      if (!isDateInRange(item.created_at, filter?.date)) return false;
      return true;
    });
  };

  const result = data
    ? filterData(data.tasks, filter)
        .map((data) => ({
          id: data.id,
          name: data.name,
          status: data.status,
          created_at: data.created_at,
          type: data.trigger_type,
          project_id: data.project_id,
        }))
        .sort(compareDateDesc('created_at'))
    : [];

  return (
    <>
      <div className="container mt-8">
        <div className="mt-4 flex items-center justify-between">
          <div className="text-base font-semibold">Tasks</div>
          {/*  */}
        </div>
        <div className="mt-4">
          <div className="mb-4 flex flex-col items-start gap-4">
            <div className="w-full">
              <ServerlessTaskSearch
                onSearch={(data) => {
                  setFilter(data);
                }}
              />
            </div>
            <Button
              variant="outline"
              onClick={() => refetch()}
              size={'sm'}
              startIcon={<RefreshCwIcon className="h-4 w-4" />}
              disabled={isLoading}
            >
              Refresh
            </Button>
          </div>
          <WrapTableAndSheet
            data={result}
            isLoading={isLoading}
            isShowProjectId
            // isShowCost
          />
        </div>
      </div>
    </>
  );
};

export default ServerlessTasks;

type WrapTableAndSheetProps = {
  data: columnsServerlessTask[];
  isLoading?: boolean;
  isShowProjectId?: boolean;
  isShowCost?: boolean;
};
export const WrapTableAndSheet = ({
  data,
  isLoading,
  isShowProjectId,
  isShowCost,
}: WrapTableAndSheetProps) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [idTask, setSelectedRow] = useState<string | null>(null);

  const handleRowClick = (id: string) => {
    setSelectedRow(id);
    setIsSheetOpen(true);
  };

  return (
    <>
      <TasksTable
        data={data}
        onClickRow={handleRowClick}
        isLoading={isLoading}
        isShowProjectId={isShowProjectId}
        isShowCost={isShowCost}
      />
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="md:min-w-[800px]">
          <SheetHeader>
            <SheetTitle>Task Details</SheetTitle>
            <Separator />
          </SheetHeader>
          <div className="h-full w-full overflow-y-auto py-4">
            <TaskDetail idTask={idTask!} />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
